import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { cancelTriggerOrder } from '../../../store/actions/triggerOrders/triggerOrdersActions';
import {
  fetchPortfolioBalances,
  portfolioFinancialPendingTransactions,
} from '../../../store/actions/portfolio/portfolioActions';
import { CANCEL_TRIGGER_ORDER_LOADING } from '../../../store/actions/triggerOrders/triggerOrdersActionConstants';
import { selectIsLoadingByActionType } from '../../../store/selectors/loadingSelectors';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import { daysActive } from '../../../util/helpers/dateHelpers';
import useGtmHook from '../../../util/hooks/useGtmHook';
import { trackGttBuyCancelled } from '../../GTT/Common/gtmEventHelper';
import tokens from '../../../assets/styles/tokens';
import SectionLoader from '../../Loader/SectionLoader';
import ModalButtons from '../../Modal/ModalButtons';
import Paragraph from '../../Paragraph/Paragraph';
import ModalBody from '../../Modal/ModalBody';

const CancelTriggerOrderText = styled(Paragraph)`
  ${tokens.type.body[300]}
  text-align: center;
`;

const CancelTriggerOrder = ({
  setError,
  handleNext,
  handleClose,
  cancelTransactionData,
  handleRefreshTransactions,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { ecommerceGtmEvent } = useGtmHook();
  const {
    paymentUid,
    accountUid,
    amount,
    content,
    productName,
    requestDate,
    quantity,
  } = cancelTransactionData;
  const isLoading = useSelector(
    selectIsLoadingByActionType(CANCEL_TRIGGER_ORDER_LOADING),
  );

  const refreshTransactions = () => {
    dispatch(portfolioFinancialPendingTransactions({ accountUid, content }));
  };

  const handleCancelTriggerOrder = () => {
    dispatch(
      cancelTriggerOrder({
        accountUid,
        triggerOrderUid: paymentUid,
        onSuccess: () => {
          setError('');
          // product_id isn't included: the pending-trigger-order data this
          // flow passes around has no product/SKU code today (only
          // ProductCaption) — add it once BE exposes one. trigger_price is
          // derived here as amount / quantity (the reserved total ÷ units),
          // consistent with how totalAtExecution is computed elsewhere for
          // the same order type. orderId is the TriggerOrderUid already used
          // as the cancel API's triggerOrderUid — the join key back to this
          // order's gtt_buy_created.
          trackGttBuyCancelled({
            ecommerceGtmEvent,
            orderId: paymentUid,
            productName,
            quantity,
            timeActiveDays: daysActive(requestDate),
            triggerPrice: quantity ? amount / quantity : null,
            orderValue: amount,
          });
          refreshTransactions();
          // Cancelling releases the reserved funds, so the Total / Reserved
          // balances held in the portfolio slice are stale until refetched.
          dispatch(fetchPortfolioBalances({ accountUid }));
          // Cancelling releases the reservation, which adds a row to the
          // transaction history — reload it through the host, which knows its
          // own content type and pagination.
          if (handleRefreshTransactions) {
            handleRefreshTransactions();
          }
          handleNext();
        },
        onError: (errorMessage) => {
          setError(errorMessage);
          handleNext();
        },
      }),
    );
  };

  return (
    <SectionLoader isLoading={isLoading}>
      <ModalBody>
        <CancelTriggerOrderText>
          {t('portfolio.pendingTransactions.cancelTriggerOrder.text', {
            amount: formatMoneyNumeral(amount),
          })}
        </CancelTriggerOrderText>
        <ModalButtons
          isHorizontal
          marginTop
          secondaryButtonProps={{
            onClick: handleCancelTriggerOrder,
            label: t(
              'portfolio.pendingTransactions.cancelTriggerOrder.yesCancel',
            ),
            textTransform: 'capitalize',
          }}
          primaryButtonProps={{
            onClick: handleClose,
            label: t(
              'portfolio.pendingTransactions.cancelTriggerOrder.noKeepIt',
            ),
            textTransform: 'capitalize',
          }}
        />
      </ModalBody>
    </SectionLoader>
  );
};

CancelTriggerOrder.propTypes = {
  setError: PropTypes.func,
  handleNext: PropTypes.func,
  handleClose: PropTypes.func,
  cancelTransactionData: PropTypes.shape({
    paymentUid: PropTypes.string,
    accountUid: PropTypes.string,
    amount: PropTypes.number,
    content: PropTypes.string,
    productName: PropTypes.string,
    requestDate: PropTypes.string,
    quantity: PropTypes.number,
  }),
  handleRefreshTransactions: PropTypes.func,
};

export default CancelTriggerOrder;
